/**
 * Display a CSS-cropped preview of a source image given a crop_box (in source-
 * pixel coordinates).
 *
 * Implementation note: this uses an `<img>` element scaled with CSS percent
 * widths and a `transform: translate()`. We tried `background-image` +
 * `background-position: X%` first; that path is broken because CSS
 * percent-based background-position uses the
 *   `position = (container - image) × percent / 100`
 * alignment formula, NOT the simple "shift by percent" interpretation. When
 * the image is larger than the container (the normal case for any non-trivial
 * crop), the formula flips signs and the preview shows the wrong region.
 *
 * The `<img>` + `transform: translate()` approach is robust because CSS
 * transform percent values ARE percent of the element's own size, so the
 * arithmetic comes out clean:
 *   - img.width = wrapper.width × (source.width / cropBox.w)
 *   - translate-x = -(cropBox.x / source.width) × 100% of img's own width
 * Substituting, source pixel `cropBox.x` lands at wrapper x=0 — exactly the
 * crop region's left edge. Same for y.
 *
 * The picker's aspect-lock keeps the cropBox aspect equal to the placement
 * spec; consumers pass a wrapper sized to that same aspect, so the crop
 * region fills the wrapper without distortion. If a future caller uses a
 * mismatched wrapper aspect, the source preserves its own aspect (height:
 * auto) and clipping/whitespace appears outside the crop region — far better
 * than the silent stretching the `background-size: X% Y%` approach produced.
 *
 * No Canvas, so no GCS CORS config needed.
 */
import React from 'react';

import type { CropBox, MediaImage } from '@/types/media';

export interface CroppedThumbnailProps {
  source: MediaImage;
  cropBox: CropBox;
  className?: string;
  /** Optional alt text override; defaults to the source's alt or filename. */
  alt?: string;
}

/** Renders into the parent's full size; the parent decides width/height. */
export function CroppedThumbnail({ source, cropBox, className, alt }: CroppedThumbnailProps) {
  // Targeted-reuse short-circuit. When the `source` MediaImage is itself a
  // Targeted (used by `TargetedImagePicker`'s reuse path, which synthesizes
  // a `PendingTargeted` whose `source` is the existing Targeted), the bytes
  // at `signed_url` are already the cropped output. There is no transform
  // math to do — render the image full-bleed via `object-fit: cover`. The
  // existing math path below would compute against the Targeted's own
  // post-crop dims with a cropBox in *parent-Approved* coordinates and
  // produce a wrong region.
  if (source.category === 'targeted') {
    return (
      <div
        role="img"
        aria-label={alt ?? source.alt_text ?? source.original_filename}
        className={className}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          src={source.signed_url}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </div>
    );
  }

  // Guard against degenerate divisors before computing percent styles. A
  // `cropBox.w === 0` (rounding-down of a near-zero ReactCrop selection) or
  // `source.width === 0` (row whose dims haven't loaded) would produce
  // `Infinity%` and render a broken preview; clamping to ≥1 keeps the math
  // finite. The resulting image is visibly wrong rather than silently empty
  // — the right signal for an upstream bug.
  const safeCropW = Math.max(1, cropBox.w);
  const safeSourceW = Math.max(1, source.width);
  const safeSourceH = Math.max(1, source.height);

  // Width such that cropBox.w source pixels equal 100% of the wrapper width.
  const imgWidthPct = (safeSourceW / safeCropW) * 100;
  // Translate by the cropBox origin in *source* coordinates, expressed as
  // percent of the rendered img's own width/height (which is what CSS
  // transform-percent uses).
  const translateXPct = (cropBox.x / safeSourceW) * 100;
  const translateYPct = (cropBox.y / safeSourceH) * 100;

  return (
    <div
      role="img"
      aria-label={alt ?? source.alt_text ?? source.original_filename}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <img
        src={source.signed_url}
        alt=""
        aria-hidden
        draggable={false}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${imgWidthPct}%`,
          height: 'auto',
          maxWidth: 'none',
          // `transform: translate(...)` percent is relative to the img's own
          // size — exactly what we want for source-pixel-accurate offsets.
          transform: `translate(-${translateXPct}%, -${translateYPct}%)`,
          transformOrigin: '0 0',
          // Defensive: prevent any inherited image styling from changing fit.
          objectFit: 'fill',
        }}
      />
    </div>
  );
}
